import { Flex, Typography } from "antd"
import cls from "classname"

import MonthStatus from "../../../VAMP/components/MonitoringProgram/MonthStatus/MonthStatus"
import { getStatusIcon } from "../../../VAMP/utils/getMonitoringMonthIcon"
import {
  getClosingLine,
  getMeaningLine,
  getNoStatusReason,
  getProgramNote,
  MONTH_STATUSES,
  STATUS_LABELS
} from "../monitoringCopy"
import { evaluateMonthLimits, getRegimeLabel, hasResolvableLimits } from "../programLimits"

import "./MonthPopover.less"

const { Text } = Typography

const MonthPopover = ({ program, month, thresholds, region, isFutureMonth }) => {
  const status = month?.status
  const { icon, variant } = getStatusIcon(status)

  const isNoStatus = status === MONTH_STATUSES.NO_STATUS
  const canResolveLimits = !isNoStatus && hasResolvableLimits({ program, region, thresholds })

  const limits = canResolveLimits ? evaluateMonthLimits({ program, month, thresholds, region }) : []
  const regimeLabel = canResolveLimits ? getRegimeLabel({ program, region, thresholds }) : null
  const note = canResolveLimits ? getProgramNote({ program, month, region }) : null
  const meaningLine = isNoStatus ? null : getMeaningLine({ status, program })
  const closingLine = isNoStatus
    ? null
    : getClosingLine({
        status,
        program,
        isEnrolled: Boolean(month?.is_enrolled),
        cleanStreakPosition: month?.clean_streak_position,
        monthNumber: month?.month
      })

  return (
    <div className="month-popover">
      <Flex align="center" gap={8} className="month-popover__head">
        <MonthStatus icon={icon} variant={variant} />
        <Text strong className="month-popover__month">
          {month?.month_name} {month?.year}
        </Text>
        <Text className={cls("status-text", variant)}>{STATUS_LABELS[status]}</Text>
        {month?.tier && <span className="month-popover__tier">{month.tier.toUpperCase()}</span>}
      </Flex>

      {isNoStatus ? (
        <Text type="secondary" className="month-popover__text">
          {getNoStatusReason({ isFutureMonth })}
        </Text>
      ) : (
        <>
          <Text type="secondary" className="month-popover__text">
            {meaningLine}
          </Text>

          {/* Every limit of the program is listed regardless of the month's status, so the merchant
              sees the full set rather than only the reached ones (§4 R4). */}
          {limits.length > 0 && (
            <div className="month-popover__limits">
              {limits.map((limit) => {
                const isViolation = limit.isReached && !limit.isMinimum

                return (
                  <div
                    key={limit.key}
                    className={cls("month-popover__limit", { reached: isViolation })}
                  >
                    {/* Ceiling limits carry their comparison operator, as the spec's limit tables
                        do (§4 R6–R8): without it "reached" is unreadable for 3DS usage, where the
                        condition is met at or below the threshold. Floors word it as
                        "1,000 minimum" instead. */}
                    {limit.label} <b>{limit.actual}</b> / {limit.limitText}{" "}
                    {/* Diverges from §4 R3, which asks for a reach-only mark carrying no
                        judgement: the design calls for the prototype's cross / tick pair. */}
                    <span className="month-popover__limit-mark">{isViolation ? "✗" : "✓"}</span>
                  </div>
                )
              })}
            </div>
          )}

          {/* The regime is named only when it is not the standard one (§4 R9). */}
          {regimeLabel && (
            <Text type="secondary" className="month-popover__text">
              Limits shown for the {regimeLabel} regime.
            </Text>
          )}

          {note && (
            <Text type="secondary" className="month-popover__text">
              {note}
            </Text>
          )}
        </>
      )}

      {closingLine && (
        <div className="month-popover__closing">
          <Text strong>{closingLine}</Text>
        </div>
      )}
    </div>
  )
}

export default MonthPopover
